import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useEventContext } from "../context/EventContext";
import { formatFromDateDetails, formatTime } from "../exports/functions";
import { useEventBannerContext } from "../context/EventBannerContext";
import { useLoader } from "../context/LoaderContext";
import { fetchEventBanner, fetchEventData } from "../api/EventApi";
import { dynamicFailed } from "../hooks/useToast";
import { marked } from "marked";
import useTab from "../hooks/useTab";
import { Fade } from "react-awesome-reveal";
import Lightbox from "yet-another-react-lightbox";
import Counter from "yet-another-react-lightbox/plugins/counter";
import Fullscreen from "yet-another-react-lightbox/plugins/fullscreen";
// import Share from "yet-another-react-lightbox/plugins/share";
import Slideshow from "yet-another-react-lightbox/plugins/slideshow";
import Video from "yet-another-react-lightbox/plugins/video";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import Thumbnails from "yet-another-react-lightbox/plugins/thumbnails";
import "yet-another-react-lightbox/styles.css";
import "yet-another-react-lightbox/plugins/counter.css";
import "yet-another-react-lightbox/plugins/thumbnails.css";

const EventsDetails = () => {
  const { setLoading } = useLoader();
  const [open, setOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const location = useLocation();
  const { eventData, setEventData } = useEventContext();
  const { eventBannerData, setEventBannerData } = useEventBannerContext();
  const tab = useTab();
  const [activeTab, setActiveTab] = useState<string>("details");

  const [currentIndex, setCurrentIndex] = useState<number>(
    location.state.index || 0
  );
  const formatEventDate = (fullDate: string) => new Date(fullDate);
  const today = new Date();
  const filteredEvents = eventData
    ?.map((event) => ({
      ...event,
      attributes: {
        ...event.attributes,
        ...formatFromDateDetails(event?.attributes?.From_Date),
        Time: formatTime(event?.attributes?.Time),
      },
    }))
    ?.filter((event) => {
      const eventDate = formatEventDate(event.attributes.From_Full_Date);
      return location.state.activeTab === "upcoming"
        ? eventDate >= today
        : eventDate < today;
    });

  const filteredEvent = filteredEvents[currentIndex];

  const convertToDate = new Date(filteredEvent?.attributes?.To_Date);
  const toDate = convertToDate?.toLocaleDateString("en-US", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });

  const eventDetailsRef = useRef<HTMLDivElement>(null);
  const moreDetailsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setLoading(true);
    if (eventData.length === 0 || eventBannerData.length === 0) {
      Promise.all([fetchEventData(), fetchEventBanner()])
        .then(([eventData, eventBannerData]) => {
          setEventData(eventData);
          setEventBannerData(eventBannerData);
          setTimeout(() => {
            setLoading(false);
          }, 1000);
        })
        .catch(() => {
          setTimeout(() => {
            setLoading(false);
          }, 1000);
          dynamicFailed("Server Error");
        });
    } else {
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  }, [
    eventBannerData.length,
    eventData.length,
    setEventBannerData,
    setEventData,
    setLoading,
  ]);

  useEffect(() => {
    eventDetailsRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [currentIndex]);

  const handleNext = () => {
    if (currentIndex < filteredEvents.length - 1) {
      setCurrentIndex((prevIndex) => prevIndex + 1);
      setActiveTab('details');
    }
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex((prevIndex) => prevIndex - 1);
      setActiveTab('details');
    }
  };

  const lightboxSlides = filteredEvent?.attributes?.Images?.data?.map(
    (image) => ({
      src: `${process.env.REACT_APP_strapiUrl}${image?.attributes?.url}`,
    })
  );
  const handleSlideClick = (index: number) => {
    setLightboxIndex(index);
    setOpen(true);
  };

  return (
    <>
      <section className="page-header" style={{ marginTop: "71px" }}>
        <div
          className="page-header-bg"
          style={{
            backgroundImage: `url(${process.env.REACT_APP_strapiUrl}${eventBannerData[0]?.attributes?.Image?.data?.attributes?.url})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        ></div>
        <div className="container">
          <div className="page-header__inner">
            <h2>Event Details</h2>
            <ul className="thm-breadcrumb list-unstyled">
              <li>
                <a href="/">Home</a>
              </li>
              <li>
                <span>/</span>
              </li>
              <li className="active">Event Details</li>
            </ul>
          </div>
        </div>
      </section>

      <div ref={eventDetailsRef}></div>

      <section className="event-details" style={{ padding: "90px 0 90px" }}>
        <div className="container">
          {filteredEvent?.attributes?.Images?.data && (
            <div
              className="section-title custom-text-center"
              style={{ marginTop: "unset" }}
            >
              <div className="quality-work__tab-box tabs-box">
                <ul
                  className="tab-buttons clearfix list-unstyled"
                  style={{ justifyContent: "center" }}
                >
                  <li
                    className={`tab-btn ${
                      activeTab === "details" ? "active-btn" : ""
                    }`}
                    onClick={() => {
                      setActiveTab("details");
                      moreDetailsRef.current?.scrollIntoView({
                        behavior: "smooth",
                      });
                    }}
                    style={{ maxWidth: tab ? "100%" : "380px" }}
                  >
                    <span>Details</span>
                  </li>
                  <li
                    className={`tab-btn ${
                      activeTab === "gallery" ? "active-btn" : ""
                    }`}
                    onClick={() => {
                      setActiveTab("gallery");
                      moreDetailsRef.current?.scrollIntoView({
                        behavior: "smooth",
                      });
                    }}
                    style={{ maxWidth: tab ? "100%" : "380px" }}
                  >
                    <span>Gallery</span>
                  </li>
                </ul>
              </div>
            </div>
          )}
          <div className="row">
            <div className="col-xl-12">
              <div className="event-details__img">
                <img
                  src={`${process.env.REACT_APP_strapiUrl}${filteredEvent?.attributes?.Image?.data?.attributes?.url}`}
                  style={{
                    objectFit: "cover",
                    height: "480px",
                  }}
                  alt="event-detail-image"
                />
                <div className="events__date">
                  <p>
                    {filteredEvent?.attributes?.From_Date} <br />{" "}
                    {filteredEvent?.attributes?.From_Month}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div ref={moreDetailsRef}></div>
          <div className="event-details__bottom">
            <div className="row">
              {activeTab === "details" ? (
                <>
                  <div className="col-xl-8 col-lg-7">
                    <div className="event-details__bottom-left">
                      <div className="event-details__bottom-content">
                        <h3 className="event-details__title">
                          {filteredEvent?.attributes?.Event_Name}
                        </h3>
                        <p
                          className="event-details__text-1"
                          style={{ textAlign: "justify" }}
                          dangerouslySetInnerHTML={{
                            __html: marked(
                              filteredEvent?.attributes?.Description || ""
                            ) as string,
                          }}
                        ></p>
                      </div>
                    </div>
                  </div>
                  <div className="col-xl-4 col-lg-5">
                    <div className="event-details__sidebar">
                      <ul className="event-details__sidebar-details list-unstyled">
                        <li>
                          <p>Time:</p>
                          <span>{filteredEvent?.attributes?.Time}</span>
                        </li>
                        <li>
                          <p>Date:</p>
                          {filteredEvent?.attributes?.From_Full_Date ===
                          toDate ? (
                            <span>
                              {filteredEvent?.attributes?.From_Full_Date}
                            </span>
                          ) : (
                            <>
                              <span>
                                {filteredEvent?.attributes?.From_Full_Date}
                              </span>
                              <span> to </span>
                              <span>{toDate}</span>
                            </>
                          )}
                        </li>
                        <li>
                          <p>Category:</p>
                          <span>{filteredEvent?.attributes?.Category}</span>
                        </li>
                        <li>
                          <p>Phone:</p>
                          <a href={`tel:${filteredEvent?.attributes?.Phone}`}>
                            {filteredEvent?.attributes?.Phone}
                          </a>
                        </li>
                        <li>
                          <p>Location:</p>
                          <span>
                            {filteredEvent?.attributes?.Event_Location}
                          </span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {filteredEvent?.attributes?.Images?.data?.map(
                    (image, index) => {
                      return (
                        <div className="col-xl-3 col-lg-6 col-md-6" key={index}>
                          <Fade
                            key={index}
                            cascade
                            damping={0.2}
                            triggerOnce={true}
                            delay={index * 200}
                          >
                            <div className="events__single">
                              <div
                                className="events__img custom-events_img"
                                style={{
                                  position: "relative",
                                  cursor: "pointer",
                                }}
                                onClick={() => handleSlideClick(index)}
                              >
                                <img
                                  src={`${process.env.REACT_APP_strapiUrl}${image?.attributes?.url}`}
                                  alt="gallery-image"
                                  style={{
                                    objectFit: "cover",
                                    // height: "200px",
                                    height: "255px",
                                  }}
                                />
                              </div>
                            </div>
                          </Fade>
                        </div>
                      );
                    }
                  )}
                </>
              )}
            </div>
          </div>

          <div className="become-volunteer__btn-container">
            {currentIndex !== 0 && (
              <button
                onClick={handlePrev}
                className="thm-btn become-volunteer__btn become-volunteer__prev-btn"
              >
                <i className="icon-arrow-left2"></i>
                Previous
              </button>
            )}
            {currentIndex !== filteredEvents.length - 1 && (
              <button
                onClick={handleNext}
                className="thm-btn become-volunteer__btn become-volunteer__next-btn"
              >
                Next
                <i className="icon-arrow-right2"></i>
              </button>
            )}
          </div>
        </div>
      </section>
      <Lightbox
        open={open}
        plugins={[
          Counter,
          Fullscreen,
          //   Share,
          Slideshow,
          Video,
          Zoom,
          Thumbnails,
        ]}
        close={() => setOpen(false)}
        slides={lightboxSlides}
        index={lightboxIndex}
      />
    </>
  );
};

export default EventsDetails;
